import React, { useState } from 'react';
import { ArrowLeftIcon } from '../ui/Icons';
import CampaignService from '../../services/CampaignService';
import { normalizeQuestionnaireData } from '../../utils/questionnairePages';

// Local status color mapping to match table badges
const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
        case 'unbearbeitet':
            return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700';
        case 'in kontakt':
            return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:text-amber-700';
        case 'absage':
            return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700';
        case 'zusage':
            return 'bg-green-100 text-green-800 border-green-400 dark:bg-green-900/30 dark:text-green-300 dark:border-green-400';
        default:
            return 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600';
    }
};

function StatusBadge({ status }) {
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(status)}`}>
            {status || 'Unbekannt'}
        </span>
    );
}

// Helper function to get question text and answer text from questionnaire data
function getQuestionAndAnswer(questionId, answer, questionnaireData) {
    // Handle the built-in availability question
    if (questionId === 'availability') {
        const availabilityOptions = ['morgens', 'vormittags', 'mittags', 'nachmittags', 'abends'];
        let answerText = 'Nicht angegeben';

        if (Array.isArray(answer) && answer.length > 0) {
            // Support both arrays of indices [0,2] and arrays of strings ['morgens','mittags']
            const selectedOptions = answer
                .map(item => {
                    if (typeof item === 'number') return availabilityOptions[item];
                    if (typeof item === 'string') return item;
                    return undefined;
                })
                .filter(option => option !== undefined && option !== null && String(option).trim() !== '')
                .join(', ');
            answerText = selectedOptions || 'Nicht angegeben';
        }

        return {
            questionText: 'Deine Erreichbarkeit',
            answerText: answerText
        };
    }
    
    // Find the question in the questionnaire data (convert both to strings for comparison)
    const question = questionnaireData?.questions?.find(q => String(q.id) === String(questionId));
    
    if (!question) {
        return {
            questionText: 'Unbekannte Frage',
            answerText: 'Nicht angegeben'
        };
    }
    
    const questionText = question.text || 'Unbekannte Frage';
    let answerText = 'Nicht angegeben';
    
    if (answer !== null && answer !== undefined && answer !== '') {
        switch (question.type) {
        case 'single-choice':
                // Answer is an index, get the option text
                if (typeof answer === 'number' && question.options && question.options[answer]) {
                    answerText = question.options[answer];
                } else if (typeof answer === 'string' && !isNaN(parseInt(answer)) && question.options && question.options[parseInt(answer)]) {
                    // Handle string numbers
                    answerText = question.options[parseInt(answer)];
                } else {
                    answerText = String(answer);
                }
                break;
            
        case 'multiple-choice':
                // Answer is an array of indices, get the option texts
                if (Array.isArray(answer) && answer.length > 0) {
                    const selectedOptions = answer
                        .map(index => question.options && question.options[index] ? question.options[index] : `Option ${index + 1}`)
                        .filter(option => option && option.trim() !== ''); // Filter out empty options
                    answerText = selectedOptions.length > 0 ? selectedOptions.join(', ') : 'Nicht angegeben';
                } else {
                    answerText = 'Nicht angegeben';
                }
                break;
            
        case 'free-text':
            case 'text':
                answerText = String(answer);
                break;

        case 'ranking':
                if (Array.isArray(answer) && question.options && answer.length > 0) {
                    const ordered = answer.map(idx => question.options[idx]).filter(Boolean);
                    answerText = ordered.map((opt, i) => `${i + 1}. ${opt}`).join('\n');
                } else {
                    answerText = 'Nicht angegeben';
                }
                break;

        case 'image':
                const at = question.answerType || 'text';
                if (at === 'single-choice' && (answer === 0 || answer)) {
                    const idx = typeof answer === 'string' ? parseInt(answer, 10) : answer;
                    answerText = question.options && question.options[idx] ? question.options[idx] : String(answer);
                } else if (at === 'multiple-choice' && Array.isArray(answer) && answer.length > 0) {
                    const selected = answer.map(i => question.options && question.options[i]).filter(Boolean);
                    answerText = selected.join(', ');
                } else {
                    answerText = String(answer);
                }
                break;
            
        default:
                answerText = String(answer);
        }
    }
    
    return {
        questionText,
        answerText
    };
}

// Same as ResultsPage: check if question has an answer for display
function isQuestionAnswered(questionId, questionType, answers) {
    const answer = answers[questionId];
    if (answer === null || answer === undefined) return false;
    if (questionType === 'multiple-choice' || questionType === 'ranking') return Array.isArray(answer) && answer.length > 0;
    if (questionType === 'single-choice' || questionType === 'text' || questionType === 'free-text' || questionType === 'image') return answer !== '' && answer !== null && answer !== undefined;
    return true;
}

export function ApplicantDetailPage({ applicant, questionnaireData, onBack, onStatusUpdate, campaignId, onApplicantUpdate }) {
    // Parse questionnaire data if it's stored as a JSON string
    const parsedQuestionnaireData = (() => {
        try {
            const parsed = typeof questionnaireData === 'string' 
                ? JSON.parse(questionnaireData) 
                : questionnaireData;
            return normalizeQuestionnaireData(parsed);
        } catch (error) {
            console.error('Error parsing questionnaire data:', error);
            return null;
        }
    })();

    // Build answers object from fragebogenResponses (same shape as submission.answers for rich display)
    const answers = (() => {
        const out = {};
        if (applicant.fragebogenResponses && Array.isArray(applicant.fragebogenResponses)) {
            applicant.fragebogenResponses.forEach(r => {
                if (r.questionId != null) out[r.questionId] = r.answer;
            });
        }
        return out;
    })();
    
    const [selectedStatus, setSelectedStatus] = useState(applicant.status);
    const [newNote, setNewNote] = useState('');
    const [notes, setNotes] = useState([
        `Erstkontakt am ${new Date(applicant.createdAt).toLocaleDateString('de-DE')} (CG)`
    ]);
    const [editingField, setEditingField] = useState(null);
    const [editValues, setEditValues] = useState({
        name: applicant.name,
        email: applicant.email,
        phone: applicant.phone,
    });
    const [originalValues, setOriginalValues] = useState({
        name: applicant.name,
        email: applicant.email,
        phone: applicant.phone,
    });
    const [history, setHistory] = useState(() => {
        const initialHistory = [
            {
                id: 1,
                timestamp: new Date(applicant.createdAt),
                user: 'System',
                action: 'Bewerbung eingegangen',
                details: 'Erstkontakt registriert'
            }
        ];
        
        // Add any existing history from the applicant data
        if (applicant.history && Array.isArray(applicant.history)) {
            return [...applicant.history, ...initialHistory];
        }
        
        return initialHistory;
    });
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', null
    const statusOptions = ['Unbearbeitet', 'In Kontakt', 'Bewerbung zurückgezogen', 'Absage', 'Zusage'];


    // Check if there are unsaved changes
    const hasUnsavedChanges = () => {
        const hasFieldChanges = (
            editValues.name !== originalValues.name ||
            editValues.email !== originalValues.email ||
            editValues.phone !== originalValues.phone
        );
        
        const hasStatusChange = selectedStatus !== applicant.status;
        
        return hasFieldChanges || hasStatusChange;
    };


    // Helper function to format timestamp for display
    const formatTimestamp = (timestamp) => {
        const now = new Date();
        const diff = now - timestamp;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);
        
        if (days === 0) {
            if (hours === 0) {
                const minutes = Math.floor(diff / (1000 * 60));
                return minutes <= 1 ? 'Gerade eben' : `Vor ${minutes} Min`;
            }
            return `Vor ${hours}h`;
        } else if (days === 1) {
            return 'Gestern, ' + timestamp.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        } else {
            return timestamp.toLocaleDateString('de-DE') + ', ' + timestamp.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        }
    };

    const handleStatusChange = (newStatus) => {
        const oldStatus = selectedStatus;
        setSelectedStatus(newStatus);
        onStatusUpdate(applicant.leadID, newStatus);
        
        // Create history entry
        const statusHistoryEntry = {
            id: Date.now(),
            timestamp: new Date(),
            user: 'bewerbung@eiei4.de',
            action: 'Status geändert',
            details: `Status von "${oldStatus}" zu "${newStatus}" geändert`,
            field: 'status',
            oldValue: oldStatus,
            newValue: newStatus
        };
        
        // Update local history
        setHistory(prev => [statusHistoryEntry, ...prev]);
        
        // Update parent component
        if (onApplicantUpdate) {
            const updatedApplicant = {
                ...applicant,
                status: newStatus,
                history: [statusHistoryEntry, ...history],
                updated: new Date().toISOString()
            };
            onApplicantUpdate(updatedApplicant);
        }
    };

    const getFunnelAnswer = (title) => {
        const funnelItem = applicant.rawFunnelData?.find(item => item.title === title);
        return funnelItem?.value || 'Nicht angegeben';
    };

    const handleSaveNote = () => {
        if (newNote.trim()) {
            const timestamp = new Date().toLocaleDateString('de-DE') + ' ' + new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
            const noteWithTimestamp = `${timestamp}: ${newNote.trim()}`;
            setNotes(prev => [noteWithTimestamp, ...prev]);
            
            // Create history entry
            const noteHistoryEntry = {
                id: Date.now(),
                timestamp: new Date(),
                user: 'bewerbung@eiei4.de',
                action: 'Notiz hinzugefügt',
                details: `Neue Notiz: "${newNote.trim()}"`,
                field: 'notes',
                oldValue: null,
                newValue: newNote.trim()
            };
            
            // Update local history
            setHistory(prev => [noteHistoryEntry, ...prev]);
            
            // Update parent component
            if (onApplicantUpdate) {
                const updatedApplicant = {
                    ...applicant,
                    notes: [noteWithTimestamp, ...notes],
                    history: [noteHistoryEntry, ...history],
                    updated: new Date().toISOString()
                };
                onApplicantUpdate(updatedApplicant);
            }
            
            setNewNote('');
        }
    };

    // Function to validate field input
    const validateField = (field, value) => {
        switch (field) {
            case 'email':
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emailRegex.test(value) || value === '';
            case 'phone':
                const phoneRegex = /^[+]?[0-9\s\-()]{7,}$/;
                return phoneRegex.test(value) || value === '';
            case 'name':
                return value.trim().length > 0 || value === '';
            default:
                return true;
        }
    };

    // Function to save field changes
    const handleSaveField = async (field) => {
        const oldValue = originalValues[field];
        const newValue = editValues[field];
        
        // Validate the field
        if (!validateField(field, newValue)) {
            const fieldLabels = {
                name: 'Name',
                email: 'E-Mail',
                phone: 'Telefonnummer'
            };
            alert(`Ungültige Eingabe für ${fieldLabels[field]}. Bitte überprüfen Sie die Eingabe.`);
            return;
        }
        
        if (oldValue !== newValue) {
            // Check if we have campaignId
            if (!campaignId) {
                alert('Fehler: Keine Kampagne-ID verfügbar. Änderung wird nur lokal gespeichert.');
                // Still update locally for now
                setOriginalValues(prev => ({ ...prev, [field]: newValue }));
                setEditingField(null);
                return;
            }

            try {
                // Prepare updated applicant data
                const updatedApplicantData = {
                    ...editValues,
                    [field]: newValue,
                    updated: new Date().toISOString()
                };


                // Update applicant in campaign
                const result = await CampaignService.updateApplicantInCampaign(
                    campaignId, 
                    applicant.leadID, 
                    updatedApplicantData
                );

                if (result.success) {
                    // Update original values
                    setOriginalValues(prev => ({ ...prev, [field]: newValue }));
                    
                    // Field labels for display
                    const fieldLabels = {
                        name: 'Name',
                        email: 'E-Mail',
                        phone: 'Telefonnummer'
                    };
                    
                    // Add to history
                    const historyEntry = {
                        id: Date.now(),
                        timestamp: new Date(),
                        user: 'bewerbung@eiei4.de',
                        action: 'Daten geändert',
                        details: `${fieldLabels[field]} von "${oldValue || 'Nicht angegeben'}" zu "${newValue || 'Nicht angegeben'}" geändert`,
                        field,
                        oldValue,
                        newValue
                    };
                    
                    // Update local history
                    setHistory(prev => [historyEntry, ...prev]);
                    
                    // Update parent component with new applicant data
                    if (onApplicantUpdate) {
                        const updatedApplicant = {
                            ...applicant,
                            ...editValues,
                            [field]: newValue,
                            updated: new Date().toISOString(),
                            history: [historyEntry, ...history]
                        };
                        onApplicantUpdate(updatedApplicant);
                    }

                } else {
                    throw new Error(result.error || 'Unbekannter Fehler beim Speichern');
                }
            } catch (error) {
                console.error('Error saving field to PocketBase:', error);
                alert(`Fehler beim Speichern: ${error.message}`);
                // Don't update original values if save failed
                return;
            }
        }
        
        setEditingField(null);
    };

    // Function to cancel field editing
    const handleCancelEdit = (field) => {
        setEditValues(prev => ({ ...prev, [field]: originalValues[field] }));
        setEditingField(null);
    };

    // Function to save all changes to PocketBase
    const handleSaveToPocketBase = async () => {

        if (!campaignId) {
            alert('Fehler: Keine Kampagne-ID verfügbar. Bitte wählen Sie zuerst eine Kampagne aus.');
            return;
        }

        setIsSaving(true);
        setSaveStatus(null);

        try {
            // Prepare updated applicant data
            const updatedApplicantData = {
                name: editValues.name,
                email: editValues.email,
                phone: editValues.phone,
                status: selectedStatus,
                notes: notes,
                history: history,
                updated: new Date().toISOString()
            };


            // Update applicant in campaign
            const result = await CampaignService.updateApplicantInCampaign(
                campaignId, 
                applicant.leadID, 
                updatedApplicantData
            );


            if (result.success) {
                // Update original values to reflect saved state
                setOriginalValues({
                    name: editValues.name,
                    email: editValues.email,
                    phone: editValues.phone,
                });

                // Add save action to history
                const saveHistoryEntry = {
                    id: Date.now(),
                    timestamp: new Date(),
                    user: 'bewerbung@eiei4.de',
                    action: 'Gespeichert',
                    details: 'Alle Änderungen wurden in PocketBase gespeichert'
                };
                setHistory(prev => [saveHistoryEntry, ...prev]);

                // Update parent component with new applicant data
                if (onApplicantUpdate) {
                    const updatedApplicant = {
                        ...applicant,
                        name: editValues.name,
                        email: editValues.email,
                        phone: editValues.phone,
                        status: selectedStatus,
                        notes: notes,
                        history: [saveHistoryEntry, ...history],
                        updated: new Date().toISOString()
                    };
                    onApplicantUpdate(updatedApplicant);
                }

                setSaveStatus('success');
                
                // Clear save status after 3 seconds
                setTimeout(() => setSaveStatus(null), 3000);
            } else {
                throw new Error(result.error || 'Unbekannter Fehler beim Speichern');
            }
        } catch (error) {
            console.error('Error saving to PocketBase:', error);
            setSaveStatus('error');
            alert(`Fehler beim Speichern: ${error.message}`);
            
            // Clear error status after 5 seconds
            setTimeout(() => setSaveStatus(null), 5000);
        } finally {
            setIsSaving(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSaveNote();
        }
    };

    const handleJsonExport = () => {
        try {
            // Create a comprehensive data export object
            const exportData = {
                // Basic applicant information
                personalData: {
                    name: editValues.name,
                    email: editValues.email,
                    phone: editValues.phone,
                    status: selectedStatus,
                    notes: editValues.notes,
                    created: applicant.created,
                    updated: applicant.updated
                },
                // Questionnaire responses
                questionnaireResponses: applicant.fragebogenResponses || {},
                // Raw funnel data (if available)
                rawFunnelData: applicant.rawFunnelData || {},
                // Campaign information
                campaign: {
                    id: applicant.campaignId || campaignId,
                    name: 'Campaign ID: ' + (applicant.campaignId || campaignId || 'Unknown')
                },
                // AI Analysis (if available)
                aiAnalysis: applicant.aiAnalysis || null,
                // Qualification evaluation (if available)
                qualificationEvaluation: applicant.qualificationEvaluation || null,
                // Export metadata
                exportMetadata: {
                    exportedAt: new Date().toISOString(),
                    exportedBy: 'HR Dashboard System',
                    purpose: 'DSGVO Data Export',
                    version: '1.0'
                }
            };

            // Create and download the JSON file
            const dataStr = JSON.stringify(exportData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = `applicant-data-${editValues.name?.replace(/\s+/g, '-').toLowerCase() || 'unknown'}-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting JSON:', error);
            alert('Fehler beim Exportieren der Daten. Bitte versuchen Sie es erneut.');
        }
    };

    const EditButton = ({ onClick, size = 'sm' }) => (
        <button
            onClick={onClick}
            className="ml-2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
        >
            <svg className={`${size === 'xs' ? 'w-3 h-3' : 'w-4 h-4'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
        </button>
    );

    const EditableField = ({ label, value, field, type = 'text', className = '' }) => {
        const isEditing = editingField === field;
        const hasChanges = isEditing && editValues[field] !== originalValues[field];
        return (
            <div className={className}>
                <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
                    <div className="flex items-center space-x-2">
                        {hasChanges && (
                            <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Ungespeichert</span>
                        )}
                    {!isEditing && <EditButton onClick={() => setEditingField(field)} />}
                    </div>
                </div>
                {isEditing ? (
                    <div className="flex items-center space-x-2 mt-1">
                        <input
                            type={type}
                            value={editValues[field]}
                            onChange={(e) => setEditValues(prev => ({ ...prev, [field]: e.target.value }))}
                            className="flex-1 px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveField(field);
                                } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    handleCancelEdit(field);
                                }
                            }}
                        />
                        <button
                            onClick={() => handleSaveField(field)}
                            className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                            title="Speichern (Enter)"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </button>
                        <button
                            onClick={() => handleCancelEdit(field)}
                            className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                            title="Abbrechen (Escape)"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                ) : (
                    <p className="text-slate-900 dark:text-slate-100 mt-1">{value || 'Nicht angegeben'}</p>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
            {/* Header */}
            <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-4">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={onBack}
                                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                aria-label="Zurück"
                            >
                                <ArrowLeftIcon className="w-5 h-5 text-slate-500" />
                            </button>
                            <div className="flex items-center space-x-3">
                                <div className="w-12 h-12 bg-linear-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                                    {applicant.name?.charAt(0) || 'B'}
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{editValues.name}</h1>
                                    <p className="text-slate-600 dark:text-slate-400">{editValues.email}</p>
                                </div>
                                <StatusBadge status={selectedStatus} />
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            {/* Unsaved Changes Indicator */}
                            {hasUnsavedChanges() && !isSaving && (
                                <div className="flex items-center space-x-2 text-amber-600 dark:text-amber-400">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <span className="text-sm font-medium">Ungespeicherte Änderungen</span>
                                </div>
                            )}
                            {/* Save Status Indicator */}
                            {saveStatus === 'success' && (
                                <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-sm font-medium">Gespeichert</span>
                                </div>
                            )}
                            {saveStatus === 'error' && (
                                <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    <span className="text-sm font-medium">Fehler</span>
                                </div>
                            )}
                            {/* Save Button */}
                            <button
                                onClick={handleSaveToPocketBase}
                                disabled={isSaving}
                                className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                                    isSaving
                                        ? 'bg-slate-400 text-slate-200 cursor-not-allowed'
                                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                }`}
                                title="Speichern"
                            >
                                {isSaving ? (
                                    <>
                                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        <span>Speichern...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        <span>Speichern</span>
                                    </>
                                )}
                            </button>
                            {/* JSON Export Button */}
                            <button
                                onClick={handleJsonExport}
                                className="px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 bg-slate-600 hover:bg-slate-700 text-white"
                                title="Daten als JSON exportieren (DSGVO)"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span>JSON Export</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="flex gap-6 justify-center">
                    {/* Left Panel */}
                    <div className="w-1/2 max-w-md space-y-6">
                        {/* Profile Section */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Profil</h2>
                            </div>
                            <div className="space-y-4">
                                <EditableField label="Name" value={editValues.name} field="name" />
                                <EditableField label="E-Mail" value={editValues.email} field="email" type="email" />
                                <EditableField label="Telefonnummer" value={editValues.phone || 'Nicht angegeben'} field="phone" type="tel" />
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Deine Erreichbarkeit</label>
                                    <p className="text-slate-900 dark:text-slate-100">
                                        {(() => {
                                            // Look for availability in fragebogenResponses (new quiz data)
                                            const availabilityResponse = applicant.fragebogenResponses?.find(r => r.questionId === 'availability');
                                            if (availabilityResponse && Array.isArray(availabilityResponse.answer) && availabilityResponse.answer.length > 0) {
                                                const availabilityOptions = ['morgens', 'vormittags', 'mittags', 'nachmittags', 'abends'];
                                                const selectedOptions = availabilityResponse.answer
                                                    .map(item => typeof item === 'number' ? availabilityOptions[item] : item)
                                                    .filter(option => option !== undefined && option !== null && String(option).trim() !== '')
                                                    .join(', ');
                                                if (selectedOptions) return selectedOptions;
                                            }
                                            // Fallback to legacy funnel data
                                            return getFunnelAnswer('Deine Erreichbarkeit');
                                        })()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Quiz Responses */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Bewerbungsantworten</h2>
                                <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm rounded-full">
                                    Vom Bewerber
                                </span>
                            </div>
                            <div className="space-y-4">
                                {parsedQuestionnaireData?.questions && parsedQuestionnaireData.questions.length > 0 ? (
                                    parsedQuestionnaireData.questions.map((q, index) => (
                                        <div key={q.id} className="space-y-2">
                                            <p className="font-semibold text-sm sm:text-base text-slate-700 dark:text-slate-300">{q.text}</p>
                                            <div className="mt-2 p-3 bg-slate-200 dark:bg-slate-700 rounded-lg border border-slate-300 dark:border-slate-600 text-sm sm:text-base text-slate-800 dark:text-slate-200 wrap-break-word">
                                                {q.type === 'multiple-choice' && isQuestionAnswered(q.id, q.type, answers) ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <div className="flex items-center gap-1">
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center"><span className="text-blue-600 font-bold text-xs">✓</span></div>
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center"><span className="text-blue-600 font-bold text-xs">✓</span></div>
                                                                <span className="text-blue-600 font-medium text-sm">...</span>
                                                            </div>
                                                            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                                                                Mehrfachauswahl – {(answers[q.id] || []).length} von {q.maxSelections || q.options?.length || 0} Optionen gewählt
                                                            </span>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {(q.options || []).map((option, optIndex) => {
                                                                const isSelected = (answers[q.id] || []).includes(optIndex);
                                                                return (
                                                                    <div key={optIndex} className={`flex items-center gap-3 p-2 rounded-lg border ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' : 'bg-white dark:bg-slate-600 border-slate-300 dark:border-slate-500'}`}>
                                                                        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${isSelected ? 'bg-blue-600 text-white' : 'border-2 border-slate-300 dark:border-slate-500'}`}>
                                                                            {isSelected && <span className="font-bold text-sm">✓</span>}
                                                                        </div>
                                                                        <span className={`font-medium ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'}`}>{option}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ) : q.type === 'single-choice' && isQuestionAnswered(q.id, q.type, answers) ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <div className="w-4 h-4 border-2 border-blue-600 rounded-full flex items-center justify-center"><div className="w-2 h-2 bg-blue-600 rounded-full"></div></div>
                                                            <span className="text-blue-600 font-medium text-sm">Einzelauswahl – 1 Option gewählt</span>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {(q.options || []).map((option, optIndex) => {
                                                                const isSelected = answers[q.id] === optIndex || (typeof answers[q.id] === 'string' && parseInt(answers[q.id], 10) === optIndex);
                                                                return (
                                                                    <div key={optIndex} className={`flex items-center gap-3 p-2 rounded-lg border ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' : 'bg-white dark:bg-slate-600 border-slate-300 dark:border-slate-500'}`}>
                                                                        <div className={`flex items-center justify-center w-6 h-6 rounded-full border-2 ${isSelected ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-300 dark:border-slate-500'}`}>
                                                                            {isSelected && <div className="w-3 h-3 bg-blue-600 rounded-full"></div>}
                                                                        </div>
                                                                        <span className={`font-medium ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'}`}>{option}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ) : q.type === 'ranking' && isQuestionAnswered(q.id, q.type, answers) ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <div className="flex items-center gap-1">
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center"><span className="text-blue-600 font-bold text-xs">1</span></div>
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center"><span className="text-blue-600 font-bold text-xs">2</span></div>
                                                                <div className="w-4 h-4 border-2 border-blue-600 rounded flex items-center justify-center"><span className="text-blue-600 font-bold text-xs">3</span></div>
                                                                <span className="text-blue-600 font-medium text-sm">...</span>
                                                            </div>
                                                            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Rangordnung – 1 = Am wichtigsten, {(answers[q.id] || []).length} = Am wenigsten wichtig</span>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {(answers[q.id] || []).map((optIndex, pos) => (
                                                                <div key={pos} className="flex items-center gap-3 p-2 bg-white dark:bg-slate-600 rounded-lg border border-slate-300 dark:border-slate-500">
                                                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-sm">{pos + 1}</div>
                                                                    <span className="text-slate-700 dark:text-slate-200 font-medium">{(q.options && q.options[optIndex]) || `Option ${optIndex + 1}`}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ) : q.type === 'image' && (q.imageUrl || q.imageDataUrl) ? (
                                                    <>
                                                        <div className="mb-2">
                                                            <img src={q.imageUrl || q.imageDataUrl} alt="Fragebild" className="max-h-32 sm:max-h-48 rounded-lg border border-slate-200 dark:border-slate-700 object-contain w-full" />
                                                        </div>
                                                        {(q.answerType === 'single-choice' || q.answerType === 'multiple-choice') && (q.options || []).length > 0 ? (
                                                            q.answerType === 'single-choice' && (answers[q.id] === 0 || answers[q.id]) ? (
                                                                <div className="space-y-2">
                                                                    {(q.options || []).map((option, optIndex) => {
                                                                        const isSelected = answers[q.id] === optIndex || (typeof answers[q.id] === 'string' && parseInt(answers[q.id], 10) === optIndex);
                                                                        return (
                                                                            <div key={optIndex} className={`flex items-center gap-3 p-2 rounded-lg border ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' : 'bg-white dark:bg-slate-600 border-slate-300 dark:border-slate-500'}`}>
                                                                                <div className={`flex items-center justify-center w-6 h-6 rounded-full border-2 ${isSelected ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-300 dark:border-slate-500'}`}>
                                                                                    {isSelected && <div className="w-3 h-3 bg-blue-600 rounded-full"></div>}
                                                                                </div>
                                                                                <span className={`font-medium ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'}`}>{option}</span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : q.answerType === 'multiple-choice' && isQuestionAnswered(q.id, q.type, answers) ? (
                                                                <div className="space-y-2">
                                                                    {(q.options || []).map((option, optIndex) => {
                                                                        const isSelected = (answers[q.id] || []).includes(optIndex);
                                                                        return (
                                                                            <div key={optIndex} className={`flex items-center gap-3 p-2 rounded-lg border ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' : 'bg-white dark:bg-slate-600 border-slate-300 dark:border-slate-500'}`}>
                                                                                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${isSelected ? 'bg-blue-600 text-white' : 'border-2 border-slate-300 dark:border-slate-500'}`}>
                                                                                    {isSelected && <span className="font-bold text-sm">✓</span>}
                                                                                </div>
                                                                                <span className={`font-medium ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'}`}>{option}</span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <span className="text-slate-400 dark:text-slate-500">Nicht angegeben</span>
                                                            )
                                                        ) : isQuestionAnswered(q.id, q.type, answers) ? (
                                                            <div className="bg-white dark:bg-slate-600 p-3 rounded border border-slate-300 dark:border-slate-500 text-slate-800 dark:text-slate-200">{typeof answers[q.id] === 'string' ? answers[q.id] : String(answers[q.id])}</div>
                                                        ) : (
                                                            <span className="text-slate-400 dark:text-slate-500">Nicht angegeben</span>
                                                        )}
                                                    </>
                                                ) : (q.type === 'text' || q.type === 'free-text') && isQuestionAnswered(q.id, q.type, answers) ? (
                                                    <div className="bg-white dark:bg-slate-600 p-3 rounded border border-slate-300 dark:border-slate-500 text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{String(answers[q.id])}</div>
                                                ) : isQuestionAnswered(q.id, q.type, answers) ? (
                                                    Array.isArray(answers[q.id]) ? (answers[q.id].map(idx => q.options && q.options[idx]).filter(Boolean).join(', ') || String(answers[q.id])) : String(answers[q.id])
                                                ) : (
                                                    <span className="text-slate-400 dark:text-slate-500">Nicht angegeben</span>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : applicant.fragebogenResponses && applicant.fragebogenResponses.length > 0 ? (
                                    applicant.fragebogenResponses
                                        .filter(response => response.questionId !== 'availability')
                                        .map((response, index) => {
                                            const { questionText, answerText } = getQuestionAndAnswer(response.questionId, response.answer, parsedQuestionnaireData);
                                            return (
                                                <div key={response.questionId || index} className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                                                    <p className="font-medium text-slate-800 dark:text-slate-200 mb-2">{questionText}</p>
                                                    <p className="text-slate-600 dark:text-slate-400">{answerText}</p>
                                                </div>
                                            );
                                        })
                                ) : (
                                    // Fallback to old funnel data if no quiz responses
                                    <>
                                        <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                                            <p className="font-medium text-slate-800 dark:text-slate-200 mb-2">Wie viel Erfahrung im Einzelhandel bringst Du mit?</p>
                                            <p className="text-slate-600 dark:text-slate-400">{getFunnelAnswer('Wie viel Erfahrung im Einzelhandel bringst Du mit?')}</p>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                                            <p className="font-medium text-slate-800 dark:text-slate-200 mb-2">Hast Du eine abgeschlossene Ausbildung im Einzelhandel?</p>
                                            <p className="text-slate-600 dark:text-slate-400">{getFunnelAnswer('Hast Du eine abgeschlossene Ausbildung im Einzelhandel?')}</p>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                                            <p className="font-medium text-slate-800 dark:text-slate-200 mb-2">Wie viele Stunden pro Woche möchtest Du maximal arbeiten?</p>
                                            <p className="text-slate-600 dark:text-slate-400">{getFunnelAnswer('Wie viele Stunden pro Woche möchtest Du maximal arbeiten?')}</p>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                                            <p className="font-medium text-slate-800 dark:text-slate-200 mb-2">Verfügst Du über sehr gute Deutschkenntnisse? (min C1-Level)</p>
                                            <p className="text-slate-600 dark:text-slate-400">{getFunnelAnswer('Verfügst Du über sehr gute Deutschkenntnisse? (min C1-Level)')}</p>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                                            <p className="font-medium text-slate-800 dark:text-slate-200 mb-2">Welcher Teilbereich interessiert dich an der Position am meisten?</p>
                                            <p className="text-slate-600 dark:text-slate-400">{getFunnelAnswer('Welcher Teilbereich interessiert dich an der Position am meisten? (Mehrfachauswahl möglich)')}</p>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                                            <p className="font-medium text-slate-800 dark:text-slate-200 mb-2">Welche Erfahrungen hast Du bislang bereits in den Teilbereichen sammeln können?</p>
                                            <p className="text-slate-600 dark:text-slate-400">{getFunnelAnswer('Welche Erfahrungen hast Du bislang bereits in den Teilbereichen sammeln können?')}</p>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                                            <p className="font-medium text-slate-800 dark:text-slate-200 mb-2">Datenschutzbestimmungen akzeptiert</p>
                                            <p className="text-slate-600 dark:text-slate-400">{getFunnelAnswer('Ich akzeptiere die Datenschutzbestimmungen.') === 'true' ? 'Ja' : 'Nein'}</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Panel */}
                    <div className="w-1/2 max-w-md space-y-6">
                        {/* Contact Data */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Kontaktdaten</h2>
                            </div>
                            <div className="space-y-4">
                                <EditableField label="Telefonnummer" value={editValues.phone} field="phone" type="tel" />
                                <EditableField label="E-Mail" value={editValues.email} field="email" type="email" />
                            </div>
                        </div>

                        {/* Status */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Bearbeiten</h2>
                                <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm rounded-full">Status</span>
                            </div>
                            <select
                                value={selectedStatus}
                                onChange={(e) => handleStatusChange(e.target.value)}
                                className="w-full px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                {statusOptions.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>

                        {/* Description */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Beschreibung</h2>
                                <EditButton onClick={() => {}} />
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4 max-h-48 overflow-y-auto">
                                {notes.map((note, index) => (
                                    <p key={index} className="text-slate-600 dark:text-slate-400 text-sm mb-3 last:mb-0">{note}</p>
                                ))}
                            </div>
                        </div>

                        {/* New Note */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">Neue Notiz</h2>
                            <div className="space-y-3">
                                <textarea
                                    value={newNote}
                                    onChange={(e) => setNewNote(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    placeholder="Notiz verfassen... (Enter zum Speichern, Shift+Enter für neue Zeile)"
                                    className="w-full px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                    rows="3"
                                />
                                <button
                                    onClick={handleSaveNote}
                                    disabled={!newNote.trim()}
                                    className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400 text-white rounded-lg transition-colors disabled:cursor-not-allowed"
                                >
                                    Notiz speichern
                                </button>
                            </div>
                        </div>

                        {/* History */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Verlauf</h2>
                                <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-sm rounded-full">Aktivitäten</span>
                            </div>
                            <div className="space-y-4 max-h-96 overflow-y-auto">
                                {history.map((entry) => (
                                    <div key={entry.id} className="flex items-start space-x-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                                    <div className="w-3 h-3 bg-orange-500 rounded-full mt-1.5"></div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{entry.user}</span>
                                                <span className="text-sm text-slate-500">{formatTimestamp(entry.timestamp)}</span>
                                        </div>
                                            <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">{entry.action}</p>
                                            <p className="text-sm text-slate-500 dark:text-slate-500">{entry.details}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
